import { useState, useRef, useEffect } from 'react'
import {
  TextField,
  IconButton,
  InputAdornment,
  Popover,
  type SxProps,
  type Theme,
} from '@mui/material'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { type Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DateInputProps {
  /** ISO date string (YYYY-MM-DD) — the committed value. */
  value: string
  /** Called with an ISO string when a valid date is selected or parsed on blur. */
  onChange: (iso: string) => void
  /** Called after blur processing completes. */
  onBlur?: () => void
  /** Forwarded to the underlying <input> for keyboard shortcuts (Enter / Escape). */
  onKeyDown?: (e: React.KeyboardEvent) => void
  autoFocus?: boolean
  size?: 'small' | 'medium'
  variant?: 'standard' | 'outlined' | 'filled'
  label?: string
  required?: boolean
  fullWidth?: boolean
  error?: boolean
  helperText?: string
  placeholder?: string
  sx?: SxProps<Theme>
  /** Styles applied to the native <input /> element. */
  inputSx?: SxProps<Theme>
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const DATE_FORMATS = [
  'MM/DD/YYYY',
  'M/D/YYYY',
  'MM-DD-YYYY',
  'M-D-YYYY',
  'YYYY-MM-DD',
]

function isoToDisplay(iso: string): string {
  if (!iso) return ''
  const d = dayjs(iso)
  return d.isValid() ? d.format('MM/DD/YYYY') : iso
}

/** Returns an ISO string (YYYY-MM-DD) or null if the text cannot be parsed. */
function parseDate(text: string): string | null {
  const trimmed = text.trim()
  if (!trimmed) return ''
  const parsed = dayjs(trimmed, DATE_FORMATS, true)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function DateInput({
  value,
  onChange,
  onBlur,
  onKeyDown,
  autoFocus = false,
  size = 'small',
  variant = 'standard',
  label,
  required = false,
  fullWidth = false,
  error = false,
  helperText,
  placeholder = 'MM/DD/YYYY',
  sx,
  inputSx,
}: DateInputProps) {
  const [focused, setFocused] = useState(false)
  const [inputText, setInputText] = useState('')
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [localError, setLocalError] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const calendarOpen = Boolean(anchorEl)

  // Auto-focus on mount when requested
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      // Small delay so MUI's layout is ready
      const id = setTimeout(() => inputRef.current?.focus(), 0)
      return () => clearTimeout(id)
    }
  }, [autoFocus])

  // ---- Derived display value ----
  const displayText: string = focused ? inputText : isoToDisplay(value)

  // ---- Handlers ----

  function handleFocus() {
    setFocused(true)
    setInputText(isoToDisplay(value))
    setLocalError(false)
  }

  function handleBlur() {
    setFocused(false)
    const trimmed = inputText.trim()

    if (!trimmed) {
      onChange('')
      setLocalError(false)
    } else {
      const parsed = parseDate(trimmed)
      if (parsed !== null) {
        onChange(parsed)
        setLocalError(false)
      } else {
        setLocalError(true)
      }
    }

    onBlur?.()
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInputText(e.target.value)
    setLocalError(false)
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault()
      inputRef.current?.blur()
    }
    onKeyDown?.(e)
  }

  function handleCalendarOpen(e: React.MouseEvent<HTMLElement>) {
    setAnchorEl(e.currentTarget)
  }

  function handleCalendarClose() {
    setAnchorEl(null)
  }

  function handleDateSelect(date: Dayjs | null) {
    if (date && date.isValid()) {
      const iso = date.format('YYYY-MM-DD')
      onChange(iso)
      setInputText(date.format('MM/DD/YYYY'))
      setLocalError(false)
    }
    handleCalendarClose()
  }

  // Dayjs value for the calendar (null when value is empty/invalid)
  const calendarValue: Dayjs | null = (() => {
    if (!value) return null
    const d = dayjs(value)
    return d.isValid() ? d : null
  })()

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TextField
        inputRef={inputRef}
        value={displayText}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        size={size}
        variant={variant}
        label={label}
        required={required}
        fullWidth={fullWidth}
        error={error || localError}
        helperText={helperText ?? (localError ? 'Invalid date (use MM/DD/YYYY)' : undefined)}
        placeholder={placeholder}
        sx={sx}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleCalendarOpen}
                sx={{ p: 0.25 }}
                aria-label="Open calendar"
              >
                <CalendarTodayIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        inputProps={{
          sx: inputSx,
        }}
      />

      <Popover
        open={calendarOpen}
        anchorEl={anchorEl}
        onClose={handleCalendarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <DateCalendar value={calendarValue} onChange={handleDateSelect} />
      </Popover>
    </LocalizationProvider>
  )
}